// src/truthy.ts
function truthy(value) {
  return Boolean(value);
}

// src/matrix.ts
var stride = function({
  v,
  m,
  width,
  offset,
  colStride
}) {
  for (let i = 0;i < v.length; i++) {
    m[i * width + (i * colStride + offset + width) % width] = v[i];
  }
  return m;
};
var identity4 = function() {
  const n = 4;
  let size = n * n;
  const m = new Array(size);
  while (size--) {
    m[size] = size % (n + 1) === 0 ? 1 : 0;
  }
  return m;
};
var rotated = function(axisVec, radians) {
  return rotatedUnitSinCos(normalize(axisVec), Math.sin(radians), Math.cos(radians));
};
var rotateX = (radians) => {
  return rotated([1, 0, 0], radians);
};
var rotateY = (radians) => {
  return rotated([0, 1, 0], radians);
};
var rotateZ = (radians) => {
  return rotated([0, 0, 1], radians);
};
var multiplyMatrices = (matrix1, matrix2) => {
  const result = new Array(16).fill(0);
  for (let i = 0;i < 4; i++) {
    for (let j = 0;j < 4; j++) {
      for (let k = 0;k < 4; k++) {
        result[i * 4 + j] += matrix1[i * 4 + k] * matrix2[k * 4 + j];
      }
    }
  }
  return result;
};
var reduceMatrices = (matrices) => {
  return matrices.filter(truthy).slice().reverse().reduce((acc, cur) => {
    return multiplyMatrices(acc, cur);
  }, identity4());
};
var aroundCenterPoint = ({
  matrix,
  x,
  y,
  z
}) => {
  return reduceMatrices([
    translateX(-x),
    translateY(-y),
    translateZ(-z),
    matrix,
    translateX(x),
    translateY(y),
    translateZ(z)
  ]);
};
var makeMatrix3dTransform = function(matrix) {
  return `matrix3d(${[
    matrix[0],
    matrix[4],
    matrix[8],
    matrix[12],
    matrix[1],
    matrix[5],
    matrix[9],
    matrix[13],
    matrix[2],
    matrix[6],
    matrix[10],
    matrix[14],
    matrix[3],
    matrix[7],
    matrix[11],
    matrix[15]
  ].join(", ")}`;
};
var interpolate = (a, b, t) => {
  return a + (b - a) * t;
};
var interpolateMatrix4d = (input, matrix1, matrix2) => {
  return [
    interpolate(matrix1[0], matrix2[0], input),
    interpolate(matrix1[1], matrix2[1], input),
    interpolate(matrix1[2], matrix2[2], input),
    interpolate(matrix1[3], matrix2[3], input),
    interpolate(matrix1[4], matrix2[4], input),
    interpolate(matrix1[5], matrix2[5], input),
    interpolate(matrix1[6], matrix2[6], input),
    interpolate(matrix1[7], matrix2[7], input),
    interpolate(matrix1[8], matrix2[8], input),
    interpolate(matrix1[9], matrix2[9], input),
    interpolate(matrix1[10], matrix2[10], input),
    interpolate(matrix1[11], matrix2[11], input),
    interpolate(matrix1[12], matrix2[12], input),
    interpolate(matrix1[13], matrix2[13], input),
    interpolate(matrix1[14], matrix2[14], input),
    interpolate(matrix1[15], matrix2[15], input)
  ];
};
var scaled = function(value) {
  const vec = typeof value === "number" ? [value, value, value] : value;
  return stride({ v: vec, m: identity4(), width: 4, offset: 0, colStride: 1 });
};
var scaleX = (x) => {
  return scaled([x, 1, 1]);
};
var scaleY = (y) => {
  return scaled([1, y, 1]);
};
var rotatedUnitSinCos = function(axisVec, sinAngle, cosAngle) {
  const x = axisVec[0];
  const y = axisVec[1];
  const z = axisVec[2];
  const c = cosAngle;
  const s = sinAngle;
  const t = 1 - c;
  return [
    t * x * x + c,
    t * x * y - s * z,
    t * x * z + s * y,
    0,
    t * x * y + s * z,
    t * y * y + c,
    t * y * z - s * x,
    0,
    t * x * z - s * y,
    t * y * z + s * x,
    t * z * z + c,
    0,
    0,
    0,
    0,
    1
  ];
};
var normalize = function(v) {
  return mulScalar(v, 1 / vectorLength(v));
};
var vectorLength = function(v) {
  return Math.sqrt(lengthSquared(v));
};
var lengthSquared = function(v) {
  return dot(v, v);
};
var dot = function(a, b) {
  if (a.length !== b.length) {
    throw new Error(`Cannot perform dot product on arrays of different length (${a.length} vs ${b.length})`);
  }
  return a.map((v, i) => {
    return v * b[i];
  }).reduce((acc, cur) => {
    return acc + cur;
  });
};
var translated = function(vec) {
  return stride({
    v: vec,
    m: identity4(),
    width: 4,
    offset: 3,
    colStride: 0
  });
};
var translateX = (x) => {
  return translated([x, 0, 0]);
};
var translateY = (y) => {
  return translated([0, y, 0]);
};
var translateZ = (z) => {
  return translated([0, 0, z]);
};
var mulScalar = function(v, s) {
  return v.map((i) => {
    return i * s;
  });
};
function multiplyMatrixAndSvgInstruction(matrix, point) {
  if (point.type === "C") {
    return {
      type: "C",
      cp1: multiplyMatrix(matrix, point.cp1),
      cp2: multiplyMatrix(matrix, point.cp2),
      point: multiplyMatrix(matrix, point.point)
    };
  }
  if (point.type === "Q") {
    return {
      type: "Q",
      cp: multiplyMatrix(matrix, point.cp),
      point: multiplyMatrix(matrix, point.point)
    };
  }
  if (point.type === "M") {
    return {
      type: "M",
      point: multiplyMatrix(matrix, point.point)
    };
  }
  if (point.type === "L") {
    return {
      type: "L",
      point: multiplyMatrix(matrix, point.point)
    };
  }
  if (point.type === "Z") {
    return {
      type: "Z",
      point: multiplyMatrix(matrix, point.point)
    };
  }
  throw new Error("Unknown instruction type: " + JSON.stringify(point));
}
var multiplyMatrix = (matrix, point) => {
  if (matrix.length !== 16 || point.length !== 4) {
    throw new Error("Invalid matrix or vector dimension");
  }
  const result = [0, 0, 0, 0];
  for (let i = 0;i < 4; i++) {
    for (let j = 0;j < 4; j++) {
      result[i] += matrix[i * 4 + j] * point[j];
    }
  }
  return result;
};
// src/3d-svg.ts
var serializeThreeDReducedInstruction = (instruction) => {
  if (instruction.type === "M") {
    return `M ${instruction.point[0]} ${instruction.point[1]}`;
  }
  if (instruction.type === "L") {
    return `L ${instruction.point[0]} ${instruction.point[1]}`;
  }
  if (instruction.type === "C") {
    return `C ${instruction.cp1[0]} ${instruction.cp1[1]} ${instruction.cp2[0]} ${instruction.cp2[1]} ${instruction.point[0]} ${instruction.point[1]}`;
  }
  if (instruction.type === "Q") {
    return `Q ${instruction.cp[0]} ${instruction.cp[1]} ${instruction.point[0]} ${instruction.point[1]}`;
  }
  if (instruction.type === "Z") {
    return "Z";
  }
  throw new Error("Unknown instruction type");
};
var threeDIntoSvgPath = (instructions) => instructions.map((instruction) => serializeThreeDReducedInstruction(instruction)).join(" ");
// src/fix-z.ts
import { reduceInstructions } from "@remotion/paths";
var turnInto3D = (instructions) => {
  let lastMove = [0, 0, 0, 1];
  const newInstructions = [];
  const reduced = reduceInstructions(instructions);
  for (let i = 0;i < reduced.length; i++) {
    const instruction = reduced[i];
    if (instruction.type === "Z") {
      newInstructions.push({
        type: "Z",
        point: lastMove
      });
    } else if (instruction.type === "M") {
      lastMove = [instruction.x, instruction.y, 0, 1];
      newInstructions.push({
        point: [instruction.x, instruction.y, 0, 1],
        type: "M"
      });
    } else if (instruction.type === "L") {
      newInstructions.push({
        type: "L",
        point: [instruction.x, instruction.y, 0, 1]
      });
    } else if (instruction.type === "C") {
      newInstructions.push({
        type: "C",
        point: [instruction.x, instruction.y, 0, 1],
        cp1: [instruction.cp1x, instruction.cp1y, 0, 1],
        cp2: [instruction.cp2x, instruction.cp2y, 0, 1]
      });
    } else {
      throw new Error("unknown");
    }
  }
  return newInstructions;
};

// src/map-face.ts
import { parsePath, reduceInstructions as reduceInstructions2 } from "@remotion/paths";
var translateSvgInstruction = (instruction, x, y, z) => {
  if (instruction.type === "M") {
    return {
      type: "M",
      point: [
        instruction.point[0] + x,
        instruction.point[1] + y,
        instruction.point[2] + z,
        instruction.point[3]
      ]
    };
  }
  if (instruction.type === "L") {
    return {
      type: "L",
      point: [
        instruction.point[0] + x,
        instruction.point[1] + y,
        instruction.point[2] + z,
        instruction.point[3]
      ]
    };
  }
  if (instruction.type === "C") {
    return {
      type: "C",
      point: [
        instruction.point[0] + x,
        instruction.point[1] + y,
        instruction.point[2] + z,
        instruction.point[3]
      ],
      cp1: [
        instruction.cp1[0] + x,
        instruction.cp1[1] + y,
        instruction.cp1[2] + z,
        instruction.cp1[3]
      ],
      cp2: [
        instruction.cp2[0] + x,
        instruction.cp2[1] + y,
        instruction.cp2[2] + z,
        instruction.cp2[3]
      ]
    };
  }
  if (instruction.type === "Q") {
    return {
      type: "Q",
      point: [
        instruction.point[0] + x,
        instruction.point[1] + y,
        instruction.point[2] + z,
        instruction.point[3]
      ],
      cp: [
        instruction.cp[0] + x,
        instruction.cp[1] + y,
        instruction.cp[2] + z,
        instruction.cp[3]
      ]
    };
  }
  if (instruction.type === "Z") {
    return {
      type: "Z",
      point: [
        instruction.point[0] + x,
        instruction.point[1] + y,
        instruction.point[2] + z,
        instruction.point[3]
      ]
    };
  }
  throw new Error("Unknown instruction type: " + JSON.stringify(instruction));
};
var transformPath = ({
  path,
  transformation
}) => {
  const parsed = parsePath(path);
  const reduced = reduceInstructions2(parsed);
  const threeD = turnInto3D(reduced);
  return threeDIntoSvgPath(threeD.map((p) => {
    return multiplyMatrixAndSvgInstruction(transformation, p);
  }));
};
var transformFace = (face, transformations) => {
  return {
    ...face,
    points: face.points.map((p) => {
      return transformations.reduce((acc, t) => {
        return multiplyMatrixAndSvgInstruction(t, acc);
      }, p);
    }),
    centerPoint: transformations.reduce((acc, t) => {
      const result = multiplyMatrix(t, acc);
      return result;
    }, face.centerPoint)
  };
};

// src/subdivide-instructions.ts
var subdivideLOrMInstruction = (from, instruction) => {
  if (instruction.type !== "L" && instruction.type !== "M") {
    throw new Error("Expected L or M instruction");
  }
  const t = 0.5;
  const q0 = [
    (1 - t) * from[0] + t * instruction.point[0],
    (1 - t) * from[1] + t * instruction.point[1],
    (1 - t) * from[2] + t * instruction.point[2],
    (1 - t) * from[3] + t * instruction.point[3]
  ];
  const curves = [
    { type: instruction.type, point: q0 },
    { type: instruction.type, point: instruction.point }
  ];
  return curves;
};
var subdivide3DCInstruction = (from, instruction) => {
  if (instruction.type !== "C") {
    throw new Error("Expected C instruction");
  }
  const t = 0.5;
  const q0 = [
    (1 - t) * from[0] + t * instruction.cp1[0],
    (1 - t) * from[1] + t * instruction.cp1[1],
    (1 - t) * from[2] + t * instruction.cp1[2],
    (1 - t) * from[3] + t * instruction.cp1[3]
  ];
  const q1 = [
    (1 - t) * instruction.cp1[0] + t * instruction.cp2[0],
    (1 - t) * instruction.cp1[1] + t * instruction.cp2[1],
    (1 - t) * instruction.cp1[2] + t * instruction.cp2[2],
    (1 - t) * instruction.cp1[3] + t * instruction.cp2[3]
  ];
  const q2 = [
    (1 - t) * instruction.cp2[0] + t * instruction.point[0],
    (1 - t) * instruction.cp2[1] + t * instruction.point[1],
    (1 - t) * instruction.cp2[2] + t * instruction.point[2],
    (1 - t) * instruction.cp2[3] + t * instruction.point[3]
  ];
  const r0 = [
    (1 - t) * q0[0] + t * q1[0],
    (1 - t) * q0[1] + t * q1[1],
    (1 - t) * q0[2] + t * q1[2],
    (1 - t) * q0[3] + t * q1[3]
  ];
  const r1 = [
    (1 - t) * q1[0] + t * q2[0],
    (1 - t) * q1[1] + t * q2[1],
    (1 - t) * q1[2] + t * q2[2],
    (1 - t) * q1[3] + t * q2[3]
  ];
  const s0 = [
    (1 - t) * r0[0] + t * r1[0],
    (1 - t) * r0[1] + t * r1[1],
    (1 - t) * r0[2] + t * r1[2],
    (1 - t) * r0[3] + t * r1[3]
  ];
  const curves = [
    { type: "C", point: s0, cp1: q0, cp2: r0 },
    { type: "C", point: instruction.point, cp1: r1, cp2: q2 }
  ];
  return curves;
};
var subdivideQInstruction = (from, instruction) => {
  if (instruction.type !== "Q") {
    throw new Error("Expected Q instruction");
  }
  const t = 0.5;
  const q0 = [
    (1 - t) * from[0] + t * instruction.cp[0],
    (1 - t) * from[1] + t * instruction.cp[1],
    (1 - t) * from[2] + t * instruction.cp[2],
    (1 - t) * from[3] + t * instruction.cp[3]
  ];
  const q1 = [
    (1 - t) * instruction.cp[0] + t * instruction.point[0],
    (1 - t) * instruction.cp[1] + t * instruction.point[1],
    (1 - t) * instruction.cp[2] + t * instruction.point[2],
    (1 - t) * instruction.cp[3] + t * instruction.point[3]
  ];
  const r0 = [
    (1 - t) * q0[0] + t * q1[0],
    (1 - t) * q0[1] + t * q1[1],
    (1 - t) * q0[2] + t * q1[2],
    (1 - t) * q0[3] + t * q1[3]
  ];
  const newInstructions = [
    { type: "Q", point: r0, cp: q0 },
    { type: "Q", point: instruction.point, cp: q1 }
  ];
  return newInstructions;
};
var subdivideInstruction = (from, instruction) => {
  if (instruction.type === "C") {
    return subdivide3DCInstruction(from, instruction);
  }
  if (instruction.type === "L" || instruction.type === "M") {
    return subdivideLOrMInstruction(from, instruction);
  }
  if (instruction.type === "Q") {
    return subdivideQInstruction(from, instruction);
  }
  if (instruction.type === "Z") {
    return [instruction];
  }
  throw new Error("Cannot subdivide instruction");
};
var subdivideInstructions = (instructions) => {
  const newInstructions = [];
  instructions.forEach((instruction, i) => {
    if (instruction.type === "M") {
      newInstructions.push(instruction);
      return;
    }
    if (instruction.type === "Z") {
      newInstructions.push(instruction);
      return;
    }
    const previousInstruction = instructions[i - 1];
    const subdivided = subdivideInstruction(previousInstruction.point, instruction);
    newInstructions.push(...subdivided);
  });
  return newInstructions;
};

// src/extrude-element.ts
var inverseInstruction = (instruction, comingFrom) => {
  if (instruction.type === "M") {
    return {
      type: "M",
      point: comingFrom
    };
  }
  if (instruction.type === "L") {
    return {
      type: "L",
      point: comingFrom
    };
  }
  if (instruction.type === "C") {
    return {
      type: "C",
      point: comingFrom,
      cp1: instruction.cp2,
      cp2: instruction.cp1
    };
  }
  if (instruction.type === "Q") {
    return {
      type: "Q",
      point: comingFrom,
      cp: instruction.cp
    };
  }
  if (instruction.type === "Z") {
    return {
      type: "L",
      point: comingFrom
    };
  }
  throw new Error("Unknown instruction type");
};
var extrudeElement = ({
  depth,
  sideColor,
  points,
  crispEdges,
  pressInDepth
}) => {
  const threeD = turnInto3D(points);
  const instructions = {
    centerPoint: [0, 0, 0, 1],
    points: subdivideInstructions(subdivideInstructions(subdivideInstructions(threeD))),
    color: "black",
    crispEdges
  };
  const unscaledBackFace = transformFace(instructions, [translateZ(depth / 2)]);
  const inbetween = unscaledBackFace.points.map((t, i) => {
    const nextInstruction = i === unscaledBackFace.points.length - 1 ? unscaledBackFace.points[0] : unscaledBackFace.points[i + 1];
    const currentPoint = t.point;
    const nextPoint = nextInstruction.point;
    const movingOver = [
      nextPoint[0],
      nextPoint[1],
      nextPoint[2] - depth + pressInDepth,
      nextPoint[3]
    ];
    const translatedInstruction = translateSvgInstruction(inverseInstruction(nextInstruction, currentPoint), 0, 0, -depth + pressInDepth);
    const newInstructions = [
      {
        type: "M",
        point: currentPoint
      },
      nextInstruction,
      nextInstruction.type === "Z" ? {
        type: "M",
        point: nextInstruction.point
      } : null,
      {
        type: "L",
        point: movingOver
      },
      translatedInstruction,
      {
        type: "L",
        point: currentPoint
      }
    ].filter(truthy);
    return {
      points: newInstructions,
      color: sideColor,
      centerPoint: [0, 0, 0, 1],
      crispEdges: true
    };
  });
  return inbetween;
};
var extrudeAndTransformElement = (options) => {
  const inbetween = extrudeElement(options);
  return inbetween.map((face) => ({
    ...transformFace(face, [options.transformations])
  }));
};
export {
  aroundCenterPoint,
  extrudeAndTransformElement,
  extrudeElement,
  interpolateMatrix4d,
  makeMatrix3dTransform,
  reduceMatrices,
  rotateX,
  rotateY,
  rotateZ,
  scaleX,
  scaleY,
  scaled,
  threeDIntoSvgPath,
  transformPath,
  translateX,
  translateY,
  translateZ
};
